import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTenant } from "@/context/TenantContext";
import { useTheme } from "@/context/ThemeContext";

interface LoadingOverlayProps {
    show: boolean;
    type?: 'authentication' | 'organization' | 'email_confirmation';
    organizationName?: string;
}

export function LoadingOverlay({ 
    show, 
    type = 'authentication',
    organizationName 
}: LoadingOverlayProps) {
    const { branding } = useTenant();
    const { theme } = useTheme();

    // Determine if we should use dark logo
    const isDarkMode = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const effectiveLogoUrl = isDarkMode && branding?.dark_logo_url ? branding.dark_logo_url : branding?.logo_url;

    if (!show) return null;

    const content = {
        authentication: {
            title: 'Verifying Authentication',
            message: 'Please wait while we verify your credentials...'
        },
        organization: {
            title: 'Switching Organization',
            message: `Loading data for ${organizationName}...`
        },
        email_confirmation: {
            title: 'Verifying Email',
            message: 'Please wait while we confirm your email address...'
        }
    }[type];

    const renderLogo = () => {
        if (!effectiveLogoUrl) {
            return (
                <span className="text-xl font-semibold">
                    {branding?.company_name || 'VoiceBot'}
                </span>
            );
        }

        return (
            <img 
                src={effectiveLogoUrl}
                alt={branding?.company_name || 'Company Logo'} 
                className="h-8 w-auto"
                onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    const parent = target.parentElement;
                    if (parent) {
                        parent.innerHTML = `<span class="text-xl font-semibold">${branding?.company_name || 'VoiceBot'}</span>`;
                    }
                }}
            />
        );
    };

    return (
        <div className={cn(
            "fixed inset-0 z-50",
            "flex items-center justify-center",
            "bg-background/95 backdrop-blur-sm",
            "transition-all duration-300 ease-in-out",
            show ? "opacity-100" : "opacity-0 pointer-events-none"
        )}>
            <div className="relative flex flex-col items-center">
                {/* Pulsing Background Circle */}
                <div className="absolute inset-0 bg-primary/5 rounded-full blur-xl animate-pulse" />
                
                {/* Content Container */}
                <div className="flex flex-col items-center space-y-4">
                    {/* Logo with subtle hover effect */}
                    <div className="relative p-4">
                        <div className="absolute inset-0 bg-primary/5 rounded-full transform transition-transform duration-300" />
                        {renderLogo()}
                    </div>

                    {/* Modern Spinner */}
                    <div className="relative">
                        <div className="absolute inset-0 bg-primary/10 rounded-full blur-md animate-pulse" />
                        <div className="relative p-3 rounded-full">
                            <Loader2 className="w-6 h-6 text-primary animate-spin" />
                        </div>
                    </div>

                    {/* Text Content with subtle animation */}
                    <div className="space-y-1.5 text-center">
                        <h3 className="text-sm font-medium text-foreground/90">
                            {content.title}
                        </h3>
                        <p className="text-xs text-muted-foreground/80">
                            {content.message}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
